import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Lock, Bot, Plane, Brain, Cpu, Rocket, Wifi, Coins, PlayCircle, GraduationCap, School, Clock, Share2, Hand, ShieldCheck, BookOpen, Wrench, Zap, TrendingUp, ClipboardList, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { ClassroomPlayer, getWatched } from "@/components/ClassroomPlayer";
import { AskZoya } from "@/components/AskZoya";
import { useZoyaAudio } from "@/lib/zoya";
import { COURSE_SECTIONS, LOCKED_COUNT } from "@/lib/classroom_courses";
import FREE from "@/lib/classroom_free.json";
import { useLang } from "@/context/LanguageContext";
import { hi } from "@/lib/classroom_hi";
import { ShareToClassroom } from "@/components/ShareToClassroom";
import { LiveZoya } from "@/components/LiveZoya";
import { useClassName } from "@/lib/classroom_live";
import { useSmartProfile } from "@/lib/smart";
import { LangPicker, LowDataToggle } from "@/components/smart/ClassControls";
import { RobotLab } from "@/components/smart/RobotLab";
import { DailyChallenge } from "@/components/smart/DailyChallenge";
import { SmartProgress } from "@/components/smart/SmartProgress";
import { Leaderboard } from "@/components/smart/Leaderboard";
import { StudentRooms } from "@/components/breakout/StudentRooms";

const ICONS = { bot: Bot, plane: Plane, brain: Brain, cpu: Cpu, rocket: Rocket, wifi: Wifi, coins: Coins };
const PREVIEW_MIN = Math.round(FREE.reduce((n, l) => n + l.duration, 0) / Math.max(FREE.length, 1) / 60);

const Hero = ({ onStart }) => {
  const { L } = useLang();
  const go = () => { onStart(); setTimeout(() => document.querySelector('[data-testid="classroom-player"]')?.scrollIntoView({ behavior: "smooth" }), 50); };
  return (
    <section className="rs-radial text-[#0A192F] relative overflow-hidden" data-testid="classroom-hero">
      <div className="absolute inset-0 rs-grid-bg opacity-40" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-7">
          <span className="rs-chip">{L("RoboSpark Virtual Robotics Classroom", "RoboSpark वर्चुअल रोबोटिक्स क्लासरूम")}</span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]" data-testid="classroom-title">
            {L("Welcome to the world of", "आपका स्वागत है")} <span className="text-[#FF2E63]">RoboSpark</span> {L("Innovative Education System", "इनोवेटिव एजुकेशन सिस्टम में")}
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl">
            {L(`Learn Humanoid Robotics, Drones and AI from Zoya Ma'am, your AI humanoid teacher, in easy Hinglish — hands-on, Class 9, Learn & Earn level. Start with ${FREE.length} free preview lessons of about ${PREVIEW_MIN} minutes each, raise your hand anytime and ask your doubts in English or 9 Indian languages, build robots in the 3D lab and take the daily challenge.`,
              `आपकी AI ह्यूमनॉइड टीचर ज़ोया मैम से आसान हिंग्लिश में ह्यूमनॉइड रोबोटिक्स, ड्रोन और AI सीखिए — हैंड्स-ऑन, कक्षा 9, लर्न एंड अर्न लेवल। लगभग ${PREVIEW_MIN} मिनट के ${FREE.length} मुफ़्त प्रीव्यू पाठों से शुरुआत करें, कभी भी हाथ उठाकर English या 9 भारतीय भाषाओं में सवाल पूछिए, 3D लैब में रोबोट बनाइए और डेली चैलेंज खेलिए।`)}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" onClick={go} className="rs-cta rounded-full px-7 h-12" data-testid="classroom-start-btn"><PlayCircle className="w-5 h-5 mr-2" />{L("Start free preview", "मुफ़्त प्रीव्यू शुरू करें")}</Button>
            <Link to="/enroll"><Button size="lg" variant="outline" className="rounded-full px-7 h-12 bg-transparent border-[#00E5FF]/50 text-[#0A192F] hover:bg-slate-100" data-testid="classroom-enroll-btn">{L(`Unlock all ${LOCKED_COUNT}+ lessons`, `सभी ${LOCKED_COUNT}+ पाठ अनलॉक करें`)}</Button></Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2"><School className="w-4 h-4 text-[#007A8A]" />{L("Schools connect via partnership", "स्कूल पार्टनरशिप से जुड़ें")}</span>
            <span className="inline-flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#007A8A]" />{L("Students enroll in minutes", "छात्र मिनटों में दाखिला लें")}</span>
            <span className="inline-flex items-center gap-2"><Coins className="w-4 h-4 text-[#007A8A]" />{L("Learn & Earn projects", "लर्न एंड अर्न प्रोजेक्ट")}</span>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_100px_-20px_rgba(255,46,99,0.5)]">
            <LiveZoya src="/classroom/zoya-l1.jpg" speaking={false} portrait={false} testid="classroom-teacher-img" />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-white via-white/80 to-transparent p-5 text-[#0A192F]">
              <div className="font-extrabold text-xl">Zoya Ma'am</div>
              <div className="text-xs font-semibold text-[#007A8A]">{L("Your RoboSpark AI humanoid teacher · Robotics, Drones & AI", "आपकी RoboSpark AI ह्यूमनॉइड टीचर · रोबोटिक्स, ड्रोन और AI")}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const LockedLibrary = () => {
  const { L } = useLang();
  return (
    <section className="bg-[#E8F0FE] text-[#0A192F] py-20" data-testid="classroom-library">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <span className="rs-chip">{L("Full course library", "पूरी कोर्स लाइब्रेरी")}</span>
        <h2 className="mt-4 text-3xl md:text-4xl font-extrabold">{L(`${LOCKED_COUNT} in-depth lessons for enrolled students`, `एनरोल्ड छात्रों के लिए ${LOCKED_COUNT} विस्तृत पाठ`)}</h2>
        <p className="mt-2 text-slate-500">{L("Every lesson is 25–45 minutes of hands-on teaching. New lessons unlock as you progress.", "हर पाठ 25–45 मिनट का हैंड्स-ऑन टीचिंग है। आगे बढ़ते ही नए पाठ खुलते हैं।")}</p>
        <div className="mt-10 space-y-10">
          {COURSE_SECTIONS.map((s) => {
            const Icon = ICONS[s.icon];
            return (
              <div key={s.id} data-testid={`course-section-${s.id}`}>
                <h3 className="flex items-center gap-3 text-xl font-bold"><Icon className="w-6 h-6 text-[#FF2E63]" />{L(s.title, s.title_hi)}<span className="text-xs font-medium text-slate-500">· {s.lessons.length} {L("lessons", "पाठ")}</span></h3>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {s.lessons.map((x, i) => (
                    <Link to="/enroll" key={`${s.id}-${i}`} data-testid={`locked-lesson-${s.id}-${i}`}
                      className="group relative p-4 rounded-2xl border border-slate-200 bg-white/70 hover:border-[#FF2E63]/60 hover:bg-slate-100 transition-colors">
                      <div className="flex items-center justify-between text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{x.min} {L("min", "मिनट")}</span>
                        <span className="inline-flex items-center gap-1 text-[#C81E4B] font-semibold"><Lock className="w-3 h-3" />{L("Enrolled only", "केवल एनरोल्ड")}</span>
                      </div>
                      <div className="mt-2 text-sm font-semibold leading-snug text-slate-700 group-hover:text-[#0A192F]">{i + 1}. {L(x.t, hi(x.t))}</div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-12 rounded-3xl border border-[#FF2E63]/40 bg-white p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="classroom-unlock-cta">
          <div>
            <div className="text-2xl font-extrabold">{L("Unlock every lesson with Zoya Ma'am", "ज़ोया मैम के साथ हर पाठ अनलॉक करें")}</div>
            <div className="text-slate-500 mt-1">{L("Enroll a student or partner your school to get full access, kits and certificates.", "पूरी एक्सेस, किट और सर्टिफ़िकेट के लिए छात्र का दाखिला करें या स्कूल को पार्टनर बनाएँ।")}</div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/enroll"><Button className="rs-cta rounded-full px-6 h-11" data-testid="unlock-enroll-btn">{L("Enroll a student", "छात्र का दाखिला")}</Button></Link>
            <Link to="/schools"><Button variant="outline" className="rounded-full px-6 h-11 bg-transparent border-[#00E5FF]/50 text-[#0A192F] hover:bg-slate-100" data-testid="unlock-school-btn">{L("Partner your school", "स्कूल पार्टनर बनाएँ")}</Button></Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const GC_STEPS = [
  [Share2, "Teacher shares a lesson", "Click “Share to Google Classroom”, pick your class and post it as an assignment.", "टीचर पाठ शेयर करें", "“Google Classroom पर शेयर करें” दबाएँ, अपनी क्लास चुनें और असाइनमेंट की तरह पोस्ट करें।"],
  [PlayCircle, "Students open it from Classroom", "The link opens this lesson directly, with captions, chapters and quiz checkpoints.", "छात्र Classroom से खोलें", "लिंक सीधे यही पाठ खोलता है — कैप्शन, चैप्टर और क्विज़ के साथ।"],
  [Hand, "Everyone talks to Zoya Ma'am", "Raise a hand anytime: she answers doubts by text and voice in English and 9 Indian languages.", "सब ज़ोया मैम से बात करें", "कभी भी हाथ उठाइए: वे टेक्स्ट और आवाज़ में English और 9 भारतीय भाषाओं में जवाब देती हैं।"],
];

const GoogleClassroom = ({ lesson }) => {
  const { L } = useLang();
  return (
    <section className="py-16 border-t border-slate-200" data-testid="google-classroom-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <span className="rs-chip">{L("For schools & teachers", "स्कूल और टीचर्स के लिए")}</span>
          <h2 className="mt-4 text-3xl md:text-4xl font-extrabold">{L("Bring Zoya Ma'am into your Google Classroom", "ज़ोया मैम को अपने Google Classroom में लाइए")}</h2>
          <p className="mt-3 text-slate-500">{L("Post any lesson to your class in one click. Students watch, take the quiz and ask Zoya Ma'am their doubts — a real classroom feel, from school or home.", "एक क्लिक में कोई भी पाठ अपनी क्लास में पोस्ट करें। छात्र देखें, क्विज़ दें और ज़ोया मैम से सवाल पूछें — स्कूल हो या घर, असली क्लास जैसा अनुभव।")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <ShareToClassroom lesson={lesson} testid="gc-section-share-btn" />
            <Link to="/student/login"><Button variant="outline" className="rounded-full h-11 px-5 bg-transparent border-[#00E5FF]/50 text-[#0A192F] hover:bg-slate-100" data-testid="gc-student-login-btn"><ShieldCheck className="w-4 h-4 mr-1" />{L("Student login", "छात्र लॉगिन")}</Button></Link>
          </div>
          <p className="mt-3 text-xs text-slate-500">{L(`Sharing: Lesson ${lesson.n} · ${lesson.title}`, `शेयर हो रहा है: पाठ ${lesson.n} · ${lesson.title_hi}`)}</p>
        </div>
        <ol className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
          {GC_STEPS.map(([Icon, t, d, tHi, dHi], i) => (
            <li key={t} className="rounded-3xl border border-slate-200 bg-white p-5" data-testid={`gc-step-${i}`}>
              <div className="flex items-center gap-2 text-[#007A8A] text-xs font-bold"><Icon className="w-5 h-5" />{L(`Step ${i + 1}`, `चरण ${i + 1}`)}</div>
              <div className="mt-3 font-bold">{L(t, tHi)}</div>
              <p className="mt-1 text-sm text-slate-500">{L(d, dHi)}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

const TABS = [["lessons", BookOpen, "Lessons", "पाठ"], ["lab", Wrench, "Robot Lab", "रोबोट लैब"], ["challenge", Zap, "Challenges", "चैलेंज"], ["progress", TrendingUp, "Progress", "प्रगति"], ["group", Users, "Group", "ग्रुप"]];

const SmartBar = ({ tab, onTab }) => {
  const { L } = useLang();
  const teacher = Boolean(localStorage.getItem("rs_teacher_token"));
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-2 sm:p-3 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between shadow-[0_8px_30px_rgb(10,25,47,0.06)]" data-testid="smart-tabs">
      <div className="grid grid-cols-5 gap-1 sm:flex sm:gap-2" role="tablist">
        {TABS.map(([id, Icon, en, h]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => onTab(id)} data-testid={`smart-tab-${id}`}
            className={`h-14 sm:h-11 px-1 sm:px-4 rounded-2xl sm:rounded-full text-[10px] sm:text-sm font-bold flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 transition-colors
              ${tab === id ? "bg-[#0A192F] text-white" : "text-slate-600 hover:bg-[#E8F0FE]"}`}><Icon className="w-4 h-4" />{L(en, h)}</button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <LangPicker />
        <LowDataToggle />
        {teacher && <Link to="/teacher#smart" data-testid="smart-teacher-panel-link" className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full border border-[#FF2E63]/50 text-sm font-bold text-[#C81E4B]"><ClipboardList className="w-4 h-4" />{L("Teacher panel", "टीचर पैनल")}</Link>}
      </div>
    </div>
  );
};

export default function VirtualClassroom() {
  const [sp, setSp] = useSearchParams();
  const [index, setIndex] = useState(() => Math.max(FREE.findIndex((l) => l.slug === sp.get("lesson")), 0));
  const [tab, setTab] = useState(() => (TABS.some((t) => t[0] === sp.get("tab")) ? sp.get("tab") : "lessons"));
  useEffect(() => {
    if (sp.get("lesson")) setTimeout(() => document.querySelector('[data-testid="classroom-player"]')?.scrollIntoView({ behavior: "smooth" }), 300);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [, force] = useState(0);
  const [hand, setHand] = useState(null);
  const [labPick, setLabPick] = useState(null);
  const control = useRef(null);
  const audio = useZoyaAudio();
  const me = useClassName();
  const [profile, reloadProfile] = useSmartProfile();
  const markWatched = (slug) => {
    const w = getWatched();
    if (!w.includes(slug)) localStorage.setItem("rs_classroom_watched", JSON.stringify([...w, slug]));
    force((n) => n + 1);
  };
  const switchTab = (t, scroll = true) => {
    if (t !== tab) { control.current?.pause(); audio.stop(); }
    if (t === "progress") reloadProfile();
    setTab(t);
    setSp((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: true });
    if (scroll) document.querySelector('[data-testid="smart-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const go = (kind, target) => {
    if (kind === "lesson") { const i = FREE.findIndex((l) => l.slug === target); if (i >= 0) setIndex(i); switchTab("lessons"); }
    else if (kind === "lab") { setLabPick({ id: target, at: Date.now() }); switchTab("lab"); }
    else switchTab("challenge");
  };
  return (
    <div className="bg-[#F0F4F8] text-[#0A192F]" data-testid="virtual-classroom-page">
      <Hero onStart={() => switchTab("lessons", false)} />
      <section className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="scroll-mt-24 mb-8" data-testid="smart-tabs-wrap"><SmartBar tab={tab} onTab={switchTab} /></div>
          <div hidden={tab !== "lessons"} data-testid="smart-panel-lessons">
            {FREE.length > 0 && <ClassroomPlayer lessons={FREE} index={index} setIndex={setIndex} onWatched={markWatched}
              onRaiseHand={setHand} controlRef={control} audio={audio} me={me} />}
            {FREE.length > 0 && <div className="mt-14"><AskZoya lesson={FREE[index]} context={hand} audio={audio} name={me.first} level={profile?.level} onBusy={() => control.current?.pause()} /></div>}
          </div>
          {tab === "lab" && <div data-testid="smart-panel-lab"><RobotLab audio={audio} initial={labPick} profile={profile} onProgress={reloadProfile} /></div>}
          {tab === "challenge" && <div data-testid="smart-panel-challenge" className="space-y-6"><DailyChallenge audio={audio} profile={profile} onProgress={reloadProfile} /><Leaderboard refresh={profile} /></div>}
          {tab === "progress" && <div data-testid="smart-panel-progress"><SmartProgress profile={profile} onGo={go} /></div>}
          {tab === "group" && <div data-testid="smart-panel-group"><StudentRooms /></div>}
        </div>
      </section>
      {FREE.length > 0 && tab === "lessons" && <GoogleClassroom lesson={FREE[index]} />}
      <LockedLibrary />
    </div>
  );
}
